/**
 * <casa-load-more>
 * ================
 * Replaces the pagination links under `casa-collection-grid` with a load more
 * button that reveals the next batch of tiles in place (CASA-366).
 *
 * Opt-in per template via the `enable_load_more` section setting — this grid
 * backs every PLP and sitewide pagination is deliberately staying put.
 *
 * Batching counts *tiles*, not products. Shopify's `paginate` works on
 * products, but the grid expands fabric/wallpaper/pillow products into one
 * tile per color variant, so a 12-product page can render 30+ tiles — which
 * read as the page ignoring its own page size. `data-batch-size` is the
 * number of tiles a press reveals: the component hides everything past the
 * first batch on connect, each press reveals one more batch from that hidden
 * reserve, and further server pages are fetched (appended hidden) only when
 * the reserve can't cover a press.
 *
 * Progressive enhancement: the pagination nav is server-rendered and this
 * element ships `hidden`. Only once it has found the grid and confirmed there
 * are tiles left to reveal does it show itself and hide the nav, so a client
 * with no JS (or a crawler) keeps real paginated links and never sees a dead
 * button. Tiles are only ever hidden from here — never server-side — because
 * a no-JS client paging through the collection must see every tile of every
 * page.
 *
 * The URL is deliberately left alone. Pushing `?page=2` would make a reload
 * render page two *instead of* pages one and two, which is worse than landing
 * back at the start — and per CASA-366 a reload resetting to the first batch
 * is acceptable.
 */
class CasaLoadMore extends HTMLElement {
  constructor() {
    super();
    this.grid = null;
    this.nav = null;
    this.button = null;
    this.label = null;
    this.currentPage = 1;
    this.totalPages = 1;
    this.batchSize = 0; // tiles per press; 0 = no windowing, a press = a page
    this.busy = false;
    this.prefetch = null; // { page, promise }
  }

  connectedCallback() {
    const wrapper = this.closest('.casa-collection-grid');
    this.grid = wrapper?.querySelector('.casa-collection-grid__list') || null;
    this.nav = wrapper?.querySelector('[data-pagination]') || null;
    this.button = this.querySelector('[data-load-more]');
    this.label = this.querySelector('[data-load-more-label]');
    this.currentPage = parseInt(this.dataset.currentPage || '1', 10);
    this.totalPages = parseInt(this.dataset.totalPages || '1', 10);
    const batch = parseInt(this.dataset.batchSize || '0', 10);
    this.batchSize = batch > 0 ? batch : 0;

    // Bail without touching anything if the page can't support the swap —
    // the server-rendered pagination stays as it is.
    if (!this.grid || !this.button) return;

    // Trim the server-rendered page down to one batch. Done here rather than
    // in Liquid so a no-JS client still gets the full page.
    if (this.batchSize > 0) {
      this.#tiles().forEach((tile, i) => {
        tile.hidden = i >= this.batchSize;
      });
    }

    // Nothing held back and nothing left to fetch: stay hidden, and leave
    // the nav alone (on a single page there is none to begin with).
    if (!this.#hasMore()) return;

    if (this.nav) this.nav.hidden = true;
    this.hidden = false;
    this.button.addEventListener('click', () => this.#onClick());

    this.#warmNextPage();
  }

  #tiles() {
    return Array.from(this.grid.children);
  }

  #hiddenTiles() {
    return this.#tiles().filter((tile) => tile.hidden);
  }

  #hasMore() {
    return this.#hiddenTiles().length > 0 || this.currentPage < this.totalPages;
  }

  /** Key a card on its product link, which carries handle + variant, so a
   *  tile straddling a page boundary is only ever appended once. */
  #cardKey(item) {
    const href = item.querySelector('a[href]')?.getAttribute('href');
    return href || '';
  }

  #pageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', this.dataset.sectionId || '');
    url.hash = '';
    return url.toString();
  }

  async #fetchPage(page) {
    const res = await fetch(this.#pageUrl(page), { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  }

  /** Fetch the next page ahead of the click so the common case is instant.
   *  Failures are swallowed — the click path refetches and surfaces the
   *  error. */
  #warmNextPage() {
    const page = this.currentPage + 1;
    if (page > this.totalPages) {
      this.prefetch = null;
      return;
    }
    const start = () => {
      this.prefetch = { page, promise: this.#fetchPage(page).catch(() => null) };
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(start, { timeout: 2000 });
    } else {
      setTimeout(start, 200);
    }
  }

  /** Fetch the next server page and append its tiles to the grid, hidden, so
   *  they join the reserve the batch window draws from. Throws on a failed
   *  fetch. Returns how many tiles were appended. */
  async #appendNextPage() {
    const page = this.currentPage + 1;
    let html = this.prefetch?.page === page ? await this.prefetch.promise : null;
    if (!html) html = await this.#fetchPage(page);

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelectorAll('.casa-collection-grid__list > li');

    const seen = new Set(this.#tiles().map((el) => this.#cardKey(el)));
    const frag = document.createDocumentFragment();
    incoming.forEach((item) => {
      const key = this.#cardKey(item);
      if (key && seen.has(key)) return;
      seen.add(key);
      item.hidden = true;
      frag.appendChild(item);
    });

    const added = frag.childElementCount;
    this.grid.appendChild(frag);
    this.currentPage = page;
    return added;
  }

  #setBusy(busy) {
    this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
    this.button.disabled = busy;
    if (this.label) this.label.textContent = busy ? 'Loading' : 'Load more';
  }

  /** Put the shopper back on real pagination rather than leaving them on a
   *  button that cannot advance. */
  #fallBackToPagination() {
    if (this.nav) this.nav.hidden = false;
    this.hidden = true;
  }

  async #onClick() {
    if (this.busy || !this.#hasMore()) return;
    this.busy = true;
    this.#setBusy(true);

    // How many tiles this press owes. Without a batch size, a press reveals
    // whatever one server page contributes (the pre-windowing behavior).
    const want = this.batchSize > 0 ? this.batchSize : Infinity;

    // Top up the hidden reserve until it can cover the press. In the
    // no-batch-size case a single page is fetched and revealed whole.
    try {
      if (this.batchSize > 0) {
        while (this.#hiddenTiles().length < want && this.currentPage < this.totalPages) {
          await this.#appendNextPage();
        }
      } else if (this.currentPage < this.totalPages) {
        await this.#appendNextPage();
      }
    } catch (err) {
      console.error('[casa-load-more] fetch failed', err);
      this.busy = false;
      this.#setBusy(false);
      this.#fallBackToPagination();
      return;
    }

    const reveal = this.#hiddenTiles().slice(0, want);
    reveal.forEach((tile) => {
      tile.hidden = false;
    });

    this.busy = false;
    this.#setBusy(false);

    if (!this.#hasMore()) {
      // Reserve drained and last page in: retire the button entirely.
      this.hidden = true;
    } else {
      this.#warmNextPage();
    }

    if (reveal.length > 0) this.#focusFirstNew(reveal[0]);
  }

  /** Move focus into the new tiles so keyboard and screen-reader users carry
   *  on from the new content — the button may have just disappeared. */
  #focusFirstNew(target) {
    if (!(target instanceof HTMLElement)) return;
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }
}

if (!customElements.get('casa-load-more')) {
  customElements.define('casa-load-more', CasaLoadMore);
}
